import createClient from 'openapi-fetch';
import type { FetchResponse, MaybeOptionalInit } from 'openapi-fetch';
import type { MediaType, PathsWithMethod, RequiredKeysOf } from 'openapi-typescript-helpers';
import type { paths } from 'shared/api/generated/api';
import request from 'shared/lib/request';

export type ApiErrorBody = {
  status: number;
  code: string;
  devMessage?: string;
  details?: unknown;
};

const isApiErrorBody = (value: unknown): value is ApiErrorBody =>
  typeof value === 'object' && value !== null && typeof (value as { code?: unknown }).code === 'string';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    const parsed = isApiErrorBody(body) ? body : undefined;
    super(parsed?.devMessage ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = parsed?.code ?? 'UNKNOWN';
    this.details = parsed?.details;
    this.body = body;
  }
}

// openapi-fetch calls this with a built Request; we delegate to `request` so auth /
// CSRF / session / failed-request-diagnostics behavior stays shared with the rest of
// the app. `request` rejects on non-2xx, but openapi-fetch needs a Response with the
// real status to produce its `{ data, error }` result — so a rejection carrying a
// response is converted back into one. A rejection with no response (network failure,
// cancellation) has no status a Response can represent, so it is thrown as an ApiError
// and propagates the way a thrown fetch does.
const fetchViaRequestWrapper = async (req: Request): Promise<Response> => {
  const method = req.method.toUpperCase();
  const url = req.url;
  const hasBody = method !== 'GET' && method !== 'HEAD';
  const bodyText = hasBody ? await req.text() : '';
  const body = bodyText ? JSON.parse(bodyText) : undefined;

  const send = () => {
    switch (method) {
      case 'GET':
        return request.get(url);
      case 'POST':
        return request.post(url, body);
      case 'PUT':
        return request.put(url, body);
      case 'PATCH':
        return request.patch(url, body);
      case 'DELETE':
        return request.del(url, body);
      default:
        throw new Error(`api client: unsupported method ${method}`);
    }
  };

  const asJsonResponse = (status: number, payload: unknown) =>
    status === 204
      ? new Response(null, { status: 204 })
      : new Response(JSON.stringify(payload ?? null), { status, headers: { 'content-type': 'application/json' } });

  try {
    const res = await send();
    return asJsonResponse(res.status, res.data);
  } catch (rejection) {
    const { status, request: response } = (rejection ?? {}) as { status?: number; request?: { data?: unknown } };
    if (!status) throw new ApiError(0, undefined);
    return asJsonResponse(status, response?.data);
  }
};

// baseUrl is origin + /gaby so the Request URL is absolute (undici/jsdom reject
// relative Request URLs) and reproduces today's /gaby/<path> URLs. The gateway adds
// /gaby; the ts-rest paths carry their own prefix (e.g. /templates).
const clientOptions = {
  baseUrl: `${window.location.origin}/gaby`,
  fetch: fetchViaRequestWrapper,
};

// Use this only to handle an error inline instead of letting it toast — it returns
// openapi-fetch's `{ data, error }` with `error` typed per endpoint. Everything else
// should go through `api`.
export const apiClientRaw = createClient<paths>(clientOptions);

type InitParam<Init> = RequiredKeysOf<Init> extends never ? [(Init & { [key: string]: unknown })?] : [Init & { [key: string]: unknown }];

type Unwrapped<TResponse extends { data?: unknown; response: Response }> = {
  data: NonNullable<TResponse['data']>;
  response: Response;
};

type UnwrappedGetMethod = <TPath extends PathsWithMethod<paths, 'get'>, TInit extends MaybeOptionalInit<paths[TPath], 'get'>>(
  url: TPath,
  ...init: InitParam<TInit>
) => Promise<Unwrapped<FetchResponse<paths[TPath]['get'], TInit, MediaType>>>;

type UnwrappedPostMethod = <TPath extends PathsWithMethod<paths, 'post'>, TInit extends MaybeOptionalInit<paths[TPath], 'post'>>(
  url: TPath,
  ...init: InitParam<TInit>
) => Promise<Unwrapped<FetchResponse<paths[TPath]['post'], TInit, MediaType>>>;

type UnwrappedPutMethod = <TPath extends PathsWithMethod<paths, 'put'>, TInit extends MaybeOptionalInit<paths[TPath], 'put'>>(
  url: TPath,
  ...init: InitParam<TInit>
) => Promise<Unwrapped<FetchResponse<paths[TPath]['put'], TInit, MediaType>>>;

type UnwrappedPatchMethod = <TPath extends PathsWithMethod<paths, 'patch'>, TInit extends MaybeOptionalInit<paths[TPath], 'patch'>>(
  url: TPath,
  ...init: InitParam<TInit>
) => Promise<Unwrapped<FetchResponse<paths[TPath]['patch'], TInit, MediaType>>>;

type UnwrappedDeleteMethod = <TPath extends PathsWithMethod<paths, 'delete'>, TInit extends MaybeOptionalInit<paths[TPath], 'delete'>>(
  url: TPath,
  ...init: InitParam<TInit>
) => Promise<Unwrapped<FetchResponse<paths[TPath]['delete'], TInit, MediaType>>>;

const unwrap = async <TResponse extends { data?: unknown; error?: unknown; response: Response }>(
  pending: Promise<TResponse>
): Promise<Unwrapped<TResponse>> => {
  const result = await pending;
  if (!result.response.ok) throw new ApiError(result.response.status, result.error);
  return { data: result.data as NonNullable<TResponse['data']>, response: result.response };
};

export const on404 =
  <T>(fallback: T) =>
  (error: unknown): T => {
    if (error instanceof ApiError && error.status === 404) return fallback;
    throw error;
  };

export const nullOn404 = on404(null);

export const api = {
  GET: ((url, ...init) => unwrap(apiClientRaw.GET(url, ...init))) as UnwrappedGetMethod,
  POST: ((url, ...init) => unwrap(apiClientRaw.POST(url, ...init))) as UnwrappedPostMethod,
  PUT: ((url, ...init) => unwrap(apiClientRaw.PUT(url, ...init))) as UnwrappedPutMethod,
  PATCH: ((url, ...init) => unwrap(apiClientRaw.PATCH(url, ...init))) as UnwrappedPatchMethod,
  DELETE: ((url, ...init) => unwrap(apiClientRaw.DELETE(url, ...init))) as UnwrappedDeleteMethod,
};
